/**
 * Shared success content layout. Renders centered content inside a
 * max-width column. Does NOT own the background color — consumers provide
 * the full-bleed `bg-primary-500` wrapper themselves.
 *
 * This is a pure content/layout component — it does NOT render a Header or
 * close button. Each consuming page is responsible for its own navigation
 * chrome (Header, safe-area insets, close buttons).
 *
 * Platform-aware layout classes (`web:mx-auto`, `web:max-w-screen-md`) are
 * embedded here rather than using a `.web.tsx` split because the divergence
 * is layout-only — the component tree is identical across platforms.
 *
 * Use `children` for full control over the content area, or pass the
 * convenience props (heading, description, riveAnimation, buttonText) for the
 * standard animation + heading + description + CTA layout.
 */
import { type Href, Link } from 'expo-router';
import { type ReactNode } from 'react';
import { twJoin } from 'tailwind-merge';

import { RiveAnimation } from '@/components/RiveAnimation';
import { Button, ButtonText } from '@/components/ui/button';
import { Heading } from '@/components/ui/heading';
import { Text } from '@/components/ui/text';
import { VStack } from '@/components/ui/vstack';
import { type RiveAnimationName } from '@/constants/riveAnimations';

type SuccessScreenChildrenProps = {
  children: ReactNode;
};

type SuccessScreenConvenienceProps = {
  children?: never;
  heading: string;
  description?: ReactNode;
  riveAnimation: RiveAnimationName;
  /** Class name for the Rive animation container. Defaults to "h-60 w-60". */
  riveClassName?: string;
  buttonText: string;
  /** Renders the button as a Link navigating to this href. */
  buttonHref?: Href;
  /** Fires when the button is pressed. Only used when `buttonHref` is not provided. */
  onButtonPress?: () => void;
};

type SuccessScreenProps = SuccessScreenChildrenProps | SuccessScreenConvenienceProps;

function DefaultContent({
  heading,
  description,
  riveAnimation,
  riveClassName = 'h-60 w-60',
  buttonText,
  buttonHref,
  onButtonPress,
}: Omit<SuccessScreenConvenienceProps, 'className'>) {
  return (
    <>
      <VStack className={twJoin('mb-12', riveClassName)}>
        <RiveAnimation name={riveAnimation} />
      </VStack>

      <Heading
        size="h1-2xl"
        className="mb-2 text-center text-typography-0"
        testID="success-screen-title">
        {heading}
      </Heading>

      <Text className="mb-8 max-w-md text-center text-typography-0">{description}</Text>

      {buttonHref ? (
        <Link href={buttonHref} asChild>
          <Button
            testID="success-screen-cta"
            variant="outline"
            className="app:w-full bg-background-0 web:w-fit"
            onPress={onButtonPress}>
            <ButtonText className="text-primary-500">{buttonText}</ButtonText>
          </Button>
        </Link>
      ) : (
        <Button
          testID="success-screen-cta"
          variant="outline"
          className="app:w-full bg-background-0 web:w-fit"
          onPress={onButtonPress}>
          <ButtonText className="text-primary-500">{buttonText}</ButtonText>
        </Button>
      )}
    </>
  );
}

export function SuccessScreen(props: SuccessScreenProps) {
  return (
    <VStack
      testID="success-screen-content"
      className="w-full grow items-center justify-center px-5 web:mx-auto web:max-w-screen-md md:px-10">
      {props.children ?? <DefaultContent {...(props as SuccessScreenConvenienceProps)} />}
    </VStack>
  );
}
